import { getEnv } from '@/utils/env';

export const THOUGHTSPOT_HOST = getEnv('VUE_APP_THOUGHTSPOT_HOST');

export const FLAT_FILE_PUBLISH_KEY = getEnv('VUE_APP_FLAT_FILE_PUBLISH_KEY');

export const FLAT_FILE_ENVIRONMENT_ID = getEnv('VUE_APP_FLAT_FILE_ENVIRONMENT_ID');

export const FLAT_FILE_COLUMN_LABEL_MAX_LENGTH = 255;

export function GET_RESOURCES_PATH(eventId) {
  return `${STATIC_ASSETS.EVENT_PREFIX}/${eventId}/${STATIC_ASSETS.RESOURCES_PATH}/`;
}

export const APP_ENVIRONMENT = getEnv('VUE_APP_ENVIRONMENT');

/**
 * Maps internal environment identifiers to standardized S3 upload prefixes.
 * Ensures consistent path naming across sg50/pr50/pr53 variants after AWS account migration.
 */
const UPLOAD_ENV_PREFIX_MAP = {
  sg50: 'alpha',
  pr50: 'prod',
  pr53: 'prod',
  dv50: 'dv50',
  alpha: 'alpha',
  prod: 'prod'
};
export const UPLOAD_PREFIX = UPLOAD_ENV_PREFIX_MAP[APP_ENVIRONMENT] ?? APP_ENVIRONMENT;

export function GET_FILLER_SLIDE_LIBRARY_S3_PATH(orgId) {
  return `${STATIC_ASSETS.FILLER_SLIDE_IMAGES}/${UPLOAD_PREFIX}/${STATIC_ASSETS.ORG_PREFIX}/${orgId}/`;
}

export const BACKGROUND_IMG_VALIDATION_DESKTOP = {
  validationType: 'image',
  desiredImageRatioRange: {
    min: '4:3',
    max: '16:9'
  },
  imageMaxWidth: 2400,
  imageMaxHeight: 2400,
  validationErrorMessage: 'Upload a 4:3 JPEG or PNG between 1,024px and 2,400px wide.'
};

export const BACKGROUND_IMG_VALIDATION_MOBILE = {
  validationType: 'image',
  desiredImageRatioRange: {
    min: '9:16',
    max: '3:4'
  },
  imageMaxWidth: 1600,
  imageMaxHeight: 1600,
  validationErrorMessage: 'Upload a 3:4 JPEG or PNG between 600px and 1,200px wide.'
};

export const LOGO_IMG_VALIDATION = {
  validationType: 'image',
  imageMaxWidth: 1600,
  imageMaxHeight: 1600,
  validationErrorMessage:
    'The file upload failed. Your image is outside the recommended resolution (maximum of 1600px in height and width). Images must be JPEG, JPG or PNG.'
};

export const VIRTUAL_BACKGROUND_VALIDATION = {
  validationType: 'image',
  imageMaxWidth: 1600,
  imageMaxHeight: 1600,
  desiredImageRatioRange: {
    min: '4:3',
    max: '16:9'
  },
  validationErrorMessage:
    'The file upload failed. Your image is outside the recommended resolution. Images must be JPEG, JPG or PNG, a maximum of 1600x900 and have 16:9 aspect ratio.'
};

export const STATIC_ASSETS = {
  ORGANIZATION_PREFIX: 'organization',
  EVENT_PREFIX: 'event',
  ORG_PREFIX: 'organization',
  LOGO_PATH: 'logo',
  BANNER_PATH: 'banner',
  SKINNED_LOGO: 'skinlogos',
  SKINNED_BANNERS: 'skinnedBanners',
  REGISTRATION_BANNER_PATH: 'registration_banner',
  SPONSOR_LOGOS_PATH: 'sponsor_logos',
  RESOURCES_PATH: 'resources',
  IMAGE_PATH: 'images',
  SITEDATA_PATH: 'regappsitedata',
  VIDEOS_PATH: 'videos',
  VIDEOS_THUMBNAIL_PATH: 'thumbnails',
  SOCIAL_MEDIA_THUMBNAIL_PATH: 'public_images',
  FILLER_SLIDE_IMAGES: 'slide-filler-images',
  CLOUDFRONT_URL: getEnv('VUE_APP_CLOUDFRONT_URL'),
  RESIZED_MEDIUM_USER_IMAGE_S3_FOLDER: 'user-resized-medium',
  ADMIN_PREFIX: 'admin'
};

export const REGEX_STRINGS = {
  email: /^[a-z0-9-_+]+([.]?[a-z0-9+_-])*@[a-z0-9-_]+([.][a-z0-9+_-]+)+$/,
  domain: /^(?!:\/\/)([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/
};

export const TRAY_APPS_NAME = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  marketo: 'Marketo',
  slack: 'Slack',
  slackPlusSalesforce: 'Slack + Salesforce',
  pardot: 'Pardot',
  webhooks: 'Webhooks',
  wistia: 'Wistia',
  eloqua: 'Eloqua'
};

export const SIMULIVE_TESTING_CHANNEL_START_BUFFER = 5 * 60 * 1000;
export const SIMULIVE_TESTING_TEST_END_BUFFER = 2 * 60 * 1000;
export const SIMULIVE_TESTING_BUFFER_TO_START_EVENT_CHANNELS = 10 * 60 * 1000;
export const SIMULIVE_TESTING_BUFFER_TIME =
  SIMULIVE_TESTING_CHANNEL_START_BUFFER +
  SIMULIVE_TESTING_TEST_END_BUFFER +
  SIMULIVE_TESTING_BUFFER_TO_START_EVENT_CHANNELS;

export const EVENT_MEDIALIVE_CHANNEL_START_TIME = 60 * 60 * 1000;

export const IMAGE_TYPES = ['.jpg', '.jpeg', '.jpe', '.jif', '.jfif', '.jfi', '.png', '.tiff', '.tif'];

export const STANDARD_PROPERTIES_MAP = {
  core_user: [
    'age',
    'city',
    'industry',
    'phone_number',
    'state',
    'street_address',
    'address_line_2',
    'zip_code',
    'linkedin_url',
    'twitter_url'
  ],
  events_eventusers: [
    'utm_campaign',
    'utm_source',
    'utm_medium',
    'utm_content',
    'utm_term',
    'utm_creative',
    'utm_keyword',
    'utm_region',
    'pronouns',
    'code_of_conduct',
    'privacy_policy',
    'terms_and_conditions',
    'health_and_safety_policy',
    'data_share_consent',
    'visa_required',
    'dietary_restrictions',
    'accessibility',
    'registrant_type'
  ]
};

export const DASHBOARD_HEARTBEAT_INTERVAL = 30000;

export const URL_PATTERN = new RegExp(
  '^(https?:\\/\\/)' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+ -@$]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
);

export const FONT_FILE_TYPES = [
  '.ttf',
  '.otf',
  '.woff',
  '.woff2',
  'TrueType',
  'font/otf',
  'font/sfnt',
  'font/ttf',
  'font/woff',
  'font/woff2',
  'application/vnd.oasis.opendocument.formula-template'
];
